/**
 * Tab 5 — "Overdue / Non-overdue Split" (Task 7, plan §5.6, Phase 10B).
 *
 * Read-only, single endpoint:
 *   GET .../overview/overdue_split/   OverviewOverdueSplitView:2037
 *       {overdue: [...], not_overdue: [...], overdue_count, not_overdue_count,
 *        truncated, max_per_bucket}
 *   bucket entry: {intellend_loan_id, disbursed_amount, lender_customer_id,
 *                  current_status}
 *
 * Layout is the plan's 50/50 horizontal split: two equal columns side by side
 * (`sm:grid-cols-2`), overdue on the left with a red-tinted header, not-overdue
 * on the right with a green-tinted header, each showing its FULL count in the
 * header (the counts are always complete even when the lists are capped).
 *
 * The endpoint caps each bucket at `max_per_bucket` (500) and flags `truncated`
 * — so a per-column "Showing first N of M" note appears only for the column(s)
 * actually affected, comparing that column's rendered length to its count
 * rather than relying on the single response-level `truncated` flag (which is
 * true if EITHER bucket overflowed).
 *
 * Loan IDs use the same <LoanIdLink> cross-tab jump as Tabs 4 and 6.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { RefreshCcw, AlertTriangle, CheckCircle2, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

import lmsStaffService, {
    OverdueSplitEntry,
    getLmsApiErrorMessage,
} from './lmsStaffApi';
import { formatAmount } from './formatters';
import { LoanIdLink } from './lmsStaffNav';

export interface OverdueSplitViewProps {
    /**
     * can_edit for `overview_overdue_split`, passed down by LmsStaffOpsHub.
     * Accepted for prop-contract consistency; Task 7 is read-only so unused.
     */
    canEdit?: boolean;
}

const BucketColumn: React.FC<{
    title: string;
    count: number;
    entries: OverdueSplitEntry[];
    tone: 'overdue' | 'not_overdue';
    isLoading: boolean;
    maxPerBucket: number;
}> = ({ title, count, entries, tone, isLoading, maxPerBucket }) => {
    const isOverdue = tone === 'overdue';
    // Per-column truncation: this bucket's rendered rows vs its full count.
    const truncated = !isLoading && count > entries.length;

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div
                className={`flex items-center justify-between gap-2 border-b px-4 py-3 ${
                    isOverdue
                        ? 'border-red-100 bg-red-50'
                        : 'border-green-100 bg-green-50'
                }`}
            >
                <h3
                    className={`flex items-center gap-2 text-sm font-semibold ${
                        isOverdue ? 'text-red-700' : 'text-green-700'
                    }`}
                >
                    {isOverdue ? (
                        <AlertTriangle className="h-4 w-4" />
                    ) : (
                        <CheckCircle2 className="h-4 w-4" />
                    )}
                    {title} ({isLoading ? '…' : count})
                </h3>
            </div>

            {truncated && (
                <p className="flex items-start gap-1.5 border-b border-amber-100 bg-amber-50 px-4 py-2 text-xs text-amber-700">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    Showing first {entries.length} of {count} (server caps each bucket at{' '}
                    {maxPerBucket}).
                </p>
            )}

            <div className="max-h-[32rem] overflow-auto">
                {isLoading ? (
                    <div className="space-y-2 p-4">
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-5 w-full" />
                    </div>
                ) : entries.length === 0 ? (
                    <div className="p-8 text-center text-sm text-gray-500">
                        No loans in this bucket.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500">
                            <tr>
                                <th className="px-3 py-2">Loan ID</th>
                                <th className="px-3 py-2">Disbursed</th>
                                <th className="px-3 py-2">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry) => (
                                <tr
                                    key={entry.intellend_loan_id}
                                    className="border-t border-gray-100"
                                >
                                    <td className="px-3 py-2">
                                        <LoanIdLink loanId={entry.intellend_loan_id} />
                                    </td>
                                    <td className="px-3 py-2 text-gray-700">
                                        {formatAmount(entry.disbursed_amount)}
                                    </td>
                                    <td className="px-3 py-2">
                                        <Badge variant="outline" className="text-xs">
                                            {entry.current_status || 'UNKNOWN'}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

const OverdueSplitView: React.FC<OverdueSplitViewProps> = () => {
    const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
        queryKey: ['lms-staff-overdue-split'],
        queryFn: async () => {
            const response = await lmsStaffService.getOverdueSplit();
            return response.data;
        },
    });

    return (
        <div className="w-full space-y-4">
            <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">
                        Overdue / Non-overdue Split
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Every active, non-simulated loan, bucketed by the same
                        is_loan_overdue() logic the customer-facing loan API uses.
                    </p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => refetch()}
                >
                    <RefreshCcw className="h-4 w-4" /> Refresh
                </Button>
            </div>

            {isError && (
                <div className="rounded-lg border border-red-100 bg-red-50 p-4 text-sm text-red-600">
                    {getLmsApiErrorMessage(
                        error,
                        'Unable to load the overdue / non-overdue split.'
                    )}
                </div>
            )}

            {/* Plan §5.6 Task 7: 50/50 horizontal split */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <BucketColumn
                    title="Overdue"
                    tone="overdue"
                    count={data?.overdue_count ?? 0}
                    entries={data?.overdue ?? []}
                    isLoading={isLoading}
                    maxPerBucket={data?.max_per_bucket ?? 500}
                />
                <BucketColumn
                    title="Not Overdue"
                    tone="not_overdue"
                    count={data?.not_overdue_count ?? 0}
                    entries={data?.not_overdue ?? []}
                    isLoading={isLoading}
                    maxPerBucket={data?.max_per_bucket ?? 500}
                />
            </div>

            {isFetching && !isLoading && (
                <p className="text-xs text-gray-400">Refreshing...</p>
            )}
        </div>
    );
};

export default OverdueSplitView;
